// Browser viewport service: the DOM-side half of the render pipeline.
//
// Reads scroll geometry directly (scrollWidth/Height, clientRect) on every
// scroll/resize, then computes visibility via pure rect intersection against
// pre-computed page rects. This keeps visibility synchronous and decoupled
// from the React render cycle — no DOM elements need to exist first.
//
// Two concerns:
//   1. Container measurement (size → ViewportMeasurement, throttled via stream)
//   2. Scroll-driven render scheduling (scroll → geometry → visible pages →
//      render plan, coalesced to one rAF per scroll burst)

import { resolveScrollPosition, type Point, type Rect, type Size } from '@st/geom'
import { Context, Effect, Layer, Queue, Stream } from 'effect'
import {
  ViewportService,
  type ViewportMeasurement,
  type ViewportScrollTarget,
  resolveCssPxPerPdfPt
} from './viewport-service'
import { resolvePinnedViewportCenterScrollPosition } from './viewport-center-pin'
import {
  type PageRect,
  type ViewerViewportState,
  resolveViewerViewportState
} from './viewer-geometry'
import { getScrollGeometry, type ScrollGeometry } from './viewport'

const areSizesEqual = (x: Size, y: Size) => {
  return x.width == y.width && x.height == y.height
}

function areNumberArraysEqual(left: number[], right: number[]) {
  if (left.length != right.length) {
    return false
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] != right[index]) {
      return false
    }
  }

  return true
}

function areScrollGeometriesEqual(left: ScrollGeometry, right: ScrollGeometry) {
  return (
    left.scrollAreaRect.x == right.scrollAreaRect.x &&
    left.scrollAreaRect.y == right.scrollAreaRect.y &&
    left.scrollAreaRect.width == right.scrollAreaRect.width &&
    left.scrollAreaRect.height == right.scrollAreaRect.height &&
    left.viewportRect.x == right.viewportRect.x &&
    left.viewportRect.y == right.viewportRect.y &&
    left.viewportRect.width == right.viewportRect.width &&
    left.viewportRect.height == right.viewportRect.height
  )
}

function areViewerViewportStatesEqual(left: ViewerViewportState, right: ViewerViewportState) {
  return (
    left.activePageNumber == right.activePageNumber &&
    areNumberArraysEqual(left.pagesToRender, right.pagesToRender)
  )
}

const SCROLL_ARRIVAL_EPSILON_PX = 1
// Keys the browser uses to scroll a focused scroll container.
const VIEWPORT_SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' '
])
// Elements that consume those keys themselves (typing, toggling) instead of
// letting them scroll the viewport.
const KEY_CONSUMING_ELEMENTS =
  'input, textarea, select, button, [contenteditable]:not([contenteditable="false"])'

// Only a key the viewport itself will act on is the user scrolling: a widget
// that handled it (defaultPrevented) or an element that types it does not.
function isViewportScrollKey(event: KeyboardEvent) {
  if (event.defaultPrevented || !VIEWPORT_SCROLL_KEYS.has(event.key)) return false
  const target = event.target as Element | null
  return target?.closest?.(KEY_CONSUMING_ELEMENTS) == null
}

export class ViewportContainerResolver extends Context.Service<
  ViewportContainerResolver,
  (containerId: string | null) => Effect.Effect<HTMLDivElement | null>
>()('ViewportContainerResolver') {}

export const BrowserViewportContainerResolverLive = Layer.succeed(
  ViewportContainerResolver,
  (containerId) =>
    Effect.sync(() => {
      if (!containerId || typeof document == 'undefined') {
        return null
      }

      const element = document.getElementById(containerId)
      if (!(element instanceof HTMLDivElement)) {
        return null
      }

      return element
    })
)

export const BrowserViewportService = Layer.effect(
  ViewportService,
  Effect.gen(function* () {
    const resolveContainer = yield* ViewportContainerResolver

    type ViewportStateSubscription = {
      pageRects: PageRect[]
      handler: (viewportState: ViewerViewportState) => void
      lastViewportState: ViewerViewportState | null
    }

    let container: HTMLDivElement | null = null
    let resizeObserver: ResizeObserver | null = null
    let scheduledGeometryFrameId: number | null = null
    let scheduledCenterPinFrameId: number | null = null
    let lastMeasuredSize: Size | null = null
    // Cleared on document open so a stale scroll area from the previous
    // document never feeds into center-pin or viewport-state calculations.
    let lastScrollGeometry: ScrollGeometry | null = null
    let lockedCssPxPerPdfPt: number | null = null
    let pushMeasuredSize: ((size: Size) => void) | null = null
    let nextViewportStateSubscriptionId = 1
    let scrollRequestGeneration = 0
    const measuredListeners = new Set<(measurement: ViewportMeasurement) => void>()
    const viewportStateSubscriptions = new Map<number, ViewportStateSubscription>()
    const scrollInvalidationListeners = new Set<() => void>()
    const userScrollListeners = new Set<() => void>()

    const notifyViewportScrollInvalidated = () => {
      scrollInvalidationListeners.forEach((listener) => {
        listener()
      })
    }

    // The user's own scrolling of the bound container. Programmatic scrollTo
    // never produces these input events, so they distinguish a user taking over
    // the viewport from the viewer moving it.
    const onUserScrollInput = () => {
      userScrollListeners.forEach((listener) => {
        listener()
      })
    }

    const onUserScrollKey = (event: KeyboardEvent) => {
      if (isViewportScrollKey(event)) onUserScrollInput()
    }

    // Content (pages, annotations) is a descendant of the container, so a press
    // on the container itself beyond its client box is on a classic scrollbar.
    // Overlay scrollbars occupy the client box and are not distinguishable.
    const onScrollbarPointerDown = (event: PointerEvent) => {
      const currentContainer = container
      if (!currentContainer || event.target != currentContainer) return
      if (
        event.offsetX < currentContainer.clientWidth &&
        event.offsetY < currentContainer.clientHeight
      ) {
        return
      }
      onUserScrollInput()
    }

    const beginViewportScrollRequest = () => {
      scrollRequestGeneration += 1
      notifyViewportScrollInvalidated()
      return scrollRequestGeneration
    }

    const resolveCurrentCssPxPerPdfPt = () =>
      resolveCssPxPerPdfPt(typeof window != 'undefined' ? window.devicePixelRatio : 1)

    const resolveLockedCssPxPerPdfPt = () => {
      if (lockedCssPxPerPdfPt != null) {
        return lockedCssPxPerPdfPt
      }

      lockedCssPxPerPdfPt = resolveCurrentCssPxPerPdfPt()
      return lockedCssPxPerPdfPt
    }

    const readMeasuredSize = (): Size | null => {
      if (!container) return null

      const rect = container.getBoundingClientRect()
      const size: Size = {
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      }
      if (size.width <= 0 || size.height <= 0) return null
      return size
    }

    const readScrollGeometry = (): ScrollGeometry | null => {
      if (!container) {
        return null
      }

      if (container.clientWidth <= 0 || container.clientHeight <= 0) {
        return null
      }

      return getScrollGeometry(container)
    }

    const toViewportMeasurement = (size: Size): ViewportMeasurement => {
      return {
        containerWidthPx: size.width,
        containerHeightPx: size.height,
        cssPxPerPdfPt: resolveLockedCssPxPerPdfPt()
      }
    }

    const publishMeasuredSize = (size: Size | null) => {
      if (!size) return
      if (lastMeasuredSize && areSizesEqual(lastMeasuredSize, size)) {
        return
      }

      lastMeasuredSize = size
      const measurement = toViewportMeasurement(size)
      measuredListeners.forEach((listener) => {
        listener(measurement)
      })
    }

    // Hot path during scrolling. Only notifies subscribers whose render plan
    // actually changed.
    const publishScrollGeometry = (scrollGeometry: ScrollGeometry | null) => {
      if (!scrollGeometry) {
        return
      }

      if (lastScrollGeometry && areScrollGeometriesEqual(lastScrollGeometry, scrollGeometry)) {
        return
      }

      lastScrollGeometry = scrollGeometry
      viewportStateSubscriptions.forEach((subscription) => {
        const nextViewportState = resolveViewerViewportState({
          pageRects: subscription.pageRects,
          viewportRect: scrollGeometry.viewportRect
        })

        if (
          subscription.lastViewportState &&
          areViewerViewportStatesEqual(subscription.lastViewportState, nextViewportState)
        ) {
          return
        }

        subscription.lastViewportState = nextViewportState
        subscription.handler(nextViewportState)
      })
    }

    const emitMeasuredSize = () => {
      const size = readMeasuredSize()
      if (!size || !pushMeasuredSize) return
      pushMeasuredSize(size)
    }

    const emitScrollGeometry = () => {
      publishScrollGeometry(readScrollGeometry())
    }

    const cancelScheduledGeometryPublish = () => {
      if (scheduledGeometryFrameId == null) {
        return
      }

      if (typeof cancelAnimationFrame == 'function') {
        cancelAnimationFrame(scheduledGeometryFrameId)
      }
      scheduledGeometryFrameId = null
    }

    const cancelScheduledCenterPin = () => {
      if (scheduledCenterPinFrameId == null) {
        return
      }

      if (typeof cancelAnimationFrame == 'function') {
        cancelAnimationFrame(scheduledCenterPinFrameId)
      }
      scheduledCenterPinFrameId = null
    }

    // Coalesce scroll events to one geometry read per frame to avoid layout thrashing.
    const scheduleGeometryPublish = () => {
      if (scheduledGeometryFrameId != null) {
        return
      }

      if (typeof requestAnimationFrame != 'function') {
        emitScrollGeometry()
        return
      }

      scheduledGeometryFrameId = requestAnimationFrame(() => {
        scheduledGeometryFrameId = null
        emitScrollGeometry()
      })
    }

    const disconnectResizeObserver = () => {
      resizeObserver?.disconnect()
      resizeObserver = null
    }

    const disconnectContainerListeners = () => {
      cancelScheduledGeometryPublish()
      cancelScheduledCenterPin()
      container?.removeEventListener('scroll', scheduleGeometryPublish)
      container?.removeEventListener('wheel', onUserScrollInput)
      container?.removeEventListener('touchmove', onUserScrollInput)
      container?.removeEventListener('keydown', onUserScrollKey)
      container?.removeEventListener('pointerdown', onScrollbarPointerDown)
      lastScrollGeometry = null
    }

    yield* Effect.forkScoped(
      Effect.gen(function* () {
        const resizeMeasurements = Stream.callback<Size>(
          (queue) => {
            return Effect.acquireRelease(
              Effect.sync(() => {
                pushMeasuredSize = (size) => Queue.offerUnsafe(queue, size)
              }),
              () => {
                return Effect.sync(() => {
                  pushMeasuredSize = null
                })
              }
            )
          },
          {
            bufferSize: 1,
            strategy: 'sliding'
          }
        ).pipe(Stream.changesWith(areSizesEqual))

        const throttledMeasurements = resizeMeasurements.pipe(
          Stream.throttle({
            cost: (measurements) => measurements.length,
            units: 1,
            duration: '33 millis',
            strategy: 'shape'
          })
        )

        yield* Stream.runForEach(throttledMeasurements, (size) => {
          return Effect.sync(() => {
            publishMeasuredSize(size)
          })
        })
      }),
      { startImmediately: true }
    )

    yield* Effect.addFinalizer(() => {
      return Effect.sync(() => {
        disconnectResizeObserver()
        disconnectContainerListeners()
        measuredListeners.clear()
        userScrollListeners.clear()
        viewportStateSubscriptions.clear()
        container = null
        lastMeasuredSize = null
        pushMeasuredSize = null
      })
    })

    return ViewportService.of({
      prepareForDocumentOpen: () => {
        return Effect.sync(() => {
          notifyViewportScrollInvalidated()
          cancelScheduledCenterPin()
          lockedCssPxPerPdfPt = null
          lastMeasuredSize = null
          lastScrollGeometry = null
          viewportStateSubscriptions.forEach((subscription) => {
            subscription.lastViewportState = null
          })
        })
      },
      bind: (containerId) => {
        return Effect.gen(function* () {
          const nextContainer = yield* resolveContainer(containerId)
          yield* Effect.sync(() => {
            if (container == nextContainer) {
              return
            }

            notifyViewportScrollInvalidated()
            disconnectResizeObserver()
            disconnectContainerListeners()
            container = nextContainer
            lastMeasuredSize = null

            if (!container) {
              return
            }

            container.addEventListener('scroll', scheduleGeometryPublish, { passive: true })
            container.addEventListener('wheel', onUserScrollInput, { passive: true })
            container.addEventListener('touchmove', onUserScrollInput, { passive: true })
            container.addEventListener('keydown', onUserScrollKey)
            container.addEventListener('pointerdown', onScrollbarPointerDown, { passive: true })

            if (typeof ResizeObserver != 'undefined') {
              resizeObserver = new ResizeObserver(() => {
                emitMeasuredSize()
                emitScrollGeometry()
              })
              resizeObserver.observe(container)
            }

            emitMeasuredSize()
            emitScrollGeometry()
          })
        })
      },
      scrollToTarget: (target: ViewportScrollTarget, behavior?: ScrollBehavior) => {
        return runScrollRequest({
          behavior,
          resolveNextScrollPosition: (currentContainer) => {
            if (currentContainer.clientWidth <= 0 || currentContainer.clientHeight <= 0) {
              return null
            }

            const scrollGeometry = getScrollGeometry(currentContainer)
            if (!targetFitsInScrollArea(scrollGeometry.scrollAreaRect, target.target)) {
              return null
            }

            return resolveScrollPosition({
              scrollArea: scrollGeometry.scrollAreaRect,
              viewport: scrollGeometry.viewportRect,
              target: target.target,
              viewportAlignment: target.viewportAlignment,
              targetAlignment: target.targetAlignment
            })
          }
        })
      },
      // Deferred to rAF so the DOM has reflowed before we read new geometry.
      pinViewportCenterAfterNextFrame: () => {
        return Effect.sync(() => {
          const prevScrollGeometry = lastScrollGeometry
          if (!container || !prevScrollGeometry) {
            return
          }

          cancelScheduledCenterPin()

          const runCenterPin = () => {
            scheduledCenterPinFrameId = null
            const nextScrollGeometry = readScrollGeometry()
            if (!container || !nextScrollGeometry) {
              return
            }

            const nextScrollPosition = resolvePinnedViewportCenterScrollPosition(
              prevScrollGeometry,
              nextScrollGeometry
            )
            if (!nextScrollPosition) {
              return
            }

            container.scrollTo({
              left: nextScrollPosition.x,
              top: nextScrollPosition.y
            })
          }

          if (typeof requestAnimationFrame != 'function') {
            runCenterPin()
            return
          }

          scheduledCenterPinFrameId = requestAnimationFrame(runCenterPin)
        })
      },
      userScrolls: Stream.callback<void>(
        (queue) =>
          Effect.acquireRelease(
            Effect.sync(() => {
              const handler = () => Queue.offerUnsafe(queue, undefined)
              userScrollListeners.add(handler)
              return handler
            }),
            (handler) =>
              Effect.sync(() => {
                userScrollListeners.delete(handler)
              })
          ),
        { bufferSize: 1, strategy: 'sliding' }
      ),
      measurements: Stream.callback<ViewportMeasurement>(
        (queue) =>
          Effect.acquireRelease(
            Effect.gen(function* () {
              const handler = (measurement: ViewportMeasurement) =>
                Queue.offerUnsafe(queue, measurement)
              measuredListeners.add(handler)

              yield* Effect.forkScoped(
                Effect.yieldNow.pipe(
                  Effect.andThen(
                    Effect.sync(() => {
                      if (!measuredListeners.has(handler)) {
                        return
                      }

                      const size = readMeasuredSize()
                      if (!size) {
                        return
                      }

                      lastMeasuredSize = size
                      handler(toViewportMeasurement(size))
                    })
                  )
                )
              ).pipe(Effect.asVoid)

              return handler
            }),
            (handler) =>
              Effect.sync(() => {
                measuredListeners.delete(handler)
              })
          ),
        { bufferSize: 1, strategy: 'sliding' }
      ),
      // Subscribers provide page rects; service handles scroll → viewport
      // state and only fires when the result changes.
      viewportStates: (pageRects: PageRect[]) =>
        Stream.callback<ViewerViewportState>(
          (queue) =>
            Effect.acquireRelease(
              Effect.gen(function* () {
                const subscriptionId = nextViewportStateSubscriptionId
                nextViewportStateSubscriptionId += 1
                const subscription: ViewportStateSubscription = {
                  pageRects,
                  handler: (state) => Queue.offerUnsafe(queue, state),
                  lastViewportState: null
                }
                viewportStateSubscriptions.set(subscriptionId, subscription)

                yield* Effect.forkScoped(
                  Effect.yieldNow.pipe(
                    Effect.andThen(
                      Effect.sync(() => {
                        const currentSubscription = viewportStateSubscriptions.get(subscriptionId)
                        if (!currentSubscription) {
                          return
                        }

                        const scrollGeometry = readScrollGeometry()
                        if (!scrollGeometry) {
                          return
                        }

                        lastScrollGeometry = scrollGeometry
                        const nextViewportState = resolveViewerViewportState({
                          pageRects: currentSubscription.pageRects,
                          viewportRect: scrollGeometry.viewportRect
                        })

                        currentSubscription.lastViewportState = nextViewportState
                        currentSubscription.handler(nextViewportState)
                      })
                    )
                  )
                ).pipe(Effect.asVoid)

                return subscriptionId
              }),
              (subscriptionId) =>
                Effect.sync(() => {
                  viewportStateSubscriptions.delete(subscriptionId)
                })
            ),
          { bufferSize: 1, strategy: 'sliding' }
        )
    })

    function runScrollRequest({
      behavior,
      resolveNextScrollPosition
    }: {
      behavior?: ScrollBehavior
      resolveNextScrollPosition: (container: HTMLDivElement) => Point | null
    }) {
      return Effect.gen(function* () {
        const requestGeneration = beginViewportScrollRequest()

        while (true) {
          const currentContainer = container
          if (!currentContainer) {
            yield* nextAnimationFrameEffect()
            continue
          }

          if (isStaleScrollRequest(requestGeneration)) {
            yield* Effect.interrupt
          }

          const nextScrollPosition = resolveNextScrollPosition(currentContainer)
          if (!nextScrollPosition) {
            yield* nextAnimationFrameEffect()
            continue
          }

          if (hasReachedScrollPosition(currentContainer, nextScrollPosition)) {
            return
          }

          yield* issueScrollAndWaitForScrollEnd({
            requestGeneration,
            currentContainer,
            nextScrollPosition,
            behavior
          })
        }
      })
    }

    function isStaleScrollRequest(requestGeneration: number) {
      return requestGeneration != scrollRequestGeneration
    }

    function hasReachedScrollPosition(currentContainer: HTMLDivElement, position: Point) {
      return (
        Math.abs(currentContainer.scrollLeft - position.x) <= SCROLL_ARRIVAL_EPSILON_PX &&
        Math.abs(currentContainer.scrollTop - position.y) <= SCROLL_ARRIVAL_EPSILON_PX
      )
    }

    function issueScrollAndWaitForScrollEnd({
      requestGeneration,
      currentContainer,
      nextScrollPosition,
      behavior
    }: {
      requestGeneration: number
      currentContainer: HTMLDivElement
      nextScrollPosition: Point
      behavior?: ScrollBehavior
    }) {
      return Effect.callback<void, never, never>((resume) => {
        let settled = false
        const cleanup = () => {
          scrollInvalidationListeners.delete(onInvalidate)
          currentContainer.removeEventListener('scrollend', onScrollEnd)
        }
        const settle = (effect: Effect.Effect<void, never, never>) => {
          if (settled) {
            return
          }

          settled = true
          cleanup()
          resume(effect)
        }
        const onInvalidate = () => {
          if (isStaleScrollRequest(requestGeneration)) {
            settle(Effect.interrupt)
            return
          }

          settle(Effect.void)
        }
        const onScrollEnd = () => {
          settle(Effect.void)
        }

        scrollInvalidationListeners.add(onInvalidate)
        currentContainer.addEventListener('scrollend', onScrollEnd)

        if (isStaleScrollRequest(requestGeneration)) {
          settle(Effect.interrupt)
        } else {
          currentContainer.scrollTo({
            left: nextScrollPosition.x,
            top: nextScrollPosition.y,
            behavior
          })
        }

        return Effect.sync(cleanup)
      })
    }
  })
)

export const BrowserViewportServiceLive = BrowserViewportService.pipe(
  Layer.provide(BrowserViewportContainerResolverLive)
)

function targetFitsInScrollArea(scrollArea: Rect, target: Rect) {
  return (
    target.x >= scrollArea.x &&
    target.y >= scrollArea.y &&
    target.x + target.width <= scrollArea.x + scrollArea.width &&
    target.y + target.height <= scrollArea.y + scrollArea.height
  )
}

const nextAnimationFrameEffect = () =>
  Effect.callback<void, never, never>((resume) => {
    if (typeof requestAnimationFrame == 'function') {
      const frameId = requestAnimationFrame(() => {
        resume(Effect.void)
      })

      return Effect.sync(() => {
        if (typeof cancelAnimationFrame == 'function') {
          cancelAnimationFrame(frameId)
        }
      })
    }

    const timeoutId = setTimeout(() => {
      resume(Effect.void)
    }, 0)

    return Effect.sync(() => {
      clearTimeout(timeoutId)
    })
  })
